import {
  DocumentNode,
  OperationVariables,
  useFragment,
  useSuspenseQuery,
} from '@apollo/client';

import { GraphQLResult, getError, getTypeOrNull } from './apollo';

type GraphQLErrorShape = {
  __typename: 'Error';
  message?: string | null;
};

export type StandardRefSuspenseHookOptions = {
  name: string;
};

type StandardRefSuspenseHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem;
};

export function withStandardRefSuspenseHook<
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  queryDoc: DocumentNode,
  dataPath: TPath,
  fragmentDoc: DocumentNode,
  fragmentName: string,
  typename: string,
): (
  options: StandardRefSuspenseHookOptions,
) => StandardRefSuspenseHookResult<TItem, TPath> {
  return (
    options: StandardRefSuspenseHookOptions,
  ): StandardRefSuspenseHookResult<TItem, TPath> => {
    const { data: fragmentData, complete } = useFragment<TItem>({
      fragment: fragmentDoc,
      fragmentName,
      from: {
        __typename: typename,
        name: options.name,
      },
    });

    const { data, error: networkError } = useSuspenseQuery<TData, TVariables>(
      queryDoc,
      {
        variables: { name: options.name } as TVariables,
        skip: complete,
        fetchPolicy: 'cache-first',
      },
    );

    if (complete) {
      return {
        [dataPath]: fragmentData as TItem,
      } as StandardRefSuspenseHookResult<TItem, TPath>;
    }

    const typedData =
      getTypeOrNull(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;
    const appError =
      getError(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;
    const error = appError ?? networkError;

    if (error) {
      throw error;
    }

    return {
      [dataPath]: typedData as TItem,
    } as StandardRefSuspenseHookResult<TItem, TPath>;
  };
}
